import { useState, type FormEvent, type ReactNode } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { Link, Navigate, useNavigate, useSearchParams } from "react-router";
import { post } from "../api/client";
import { keys, useMe } from "../api/hooks";
import { Button, Card, ErrorBox, Field, Input } from "../components/ui";

function AuthShell({ title, children }: { title: string; children: ReactNode }) {
  return (
    <div className="mx-auto mt-10 max-w-sm">
      <div className="mb-6 flex items-center justify-center gap-2 text-lg font-semibold">
        <img src="/favicon.svg" alt="" className="size-7" /> Markets
      </div>
      <Card>
        <h1 className="mb-4 text-xl font-semibold">{title}</h1>
        {children}
      </Card>
    </div>
  );
}

/** Only allow in-app redirects after login. */
function safeNext(next: string | null): string {
  return next && next.startsWith("/") && !next.startsWith("//") ? next : "/";
}

export function LoginPage() {
  const [params] = useSearchParams();
  const me = useMe();
  const qc = useQueryClient();
  const navigate = useNavigate();
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState<unknown>(null);
  const [busy, setBusy] = useState(false);
  const next = safeNext(params.get("next"));

  if (me.data) return <Navigate to={next} replace />;

  async function submit(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await post("/auth/login", { username, password });
      await qc.invalidateQueries({ queryKey: keys.me });
      navigate(next, { replace: true });
    } catch (err) {
      setError(err);
    } finally {
      setBusy(false);
    }
  }

  return (
    <AuthShell title="Log in">
      <form onSubmit={submit} className="space-y-4">
        <Field label="Username">
          <Input autoComplete="username" autoCapitalize="none" value={username} onChange={(e) => setUsername(e.target.value)} required />
        </Field>
        <Field label="Password">
          <Input type="password" autoComplete="current-password" value={password} onChange={(e) => setPassword(e.target.value)} required />
        </Field>
        <ErrorBox error={error} />
        <Button type="submit" disabled={busy} className="w-full">{busy ? "Logging in…" : "Log in"}</Button>
      </form>
      <p className="mt-4 text-sm text-muted">New here? You'll need an invite link from the admin.</p>
    </AuthShell>
  );
}

export function SignupPage() {
  const [params] = useSearchParams();
  const [invite, setInvite] = useState(params.get("invite") ?? "");
  const [username, setUsername] = useState("");
  const [displayName, setDisplayName] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState<unknown>(null);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  async function submit(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await post("/auth/signup", { inviteCode: invite.trim(), username, displayName: displayName || username, password });
      setDone(true);
    } catch (err) {
      setError(err);
    } finally {
      setBusy(false);
    }
  }

  if (done) {
    return (
      <AuthShell title="Almost there">
        <p className="text-sm">Your account is created and waiting for the admin to approve it. Once approved you'll get 650 chips to start.</p>
        <Link to="/login" className="mt-4 inline-block text-sm text-accent">Go to login</Link>
      </AuthShell>
    );
  }

  return (
    <AuthShell title="Join">
      <form onSubmit={submit} className="space-y-4">
        <Field label="Invite code">
          <Input value={invite} onChange={(e) => setInvite(e.target.value)} required autoCapitalize="none" />
        </Field>
        <Field label="Username" hint="3–20 letters, digits or underscores. Everyone sees it.">
          <Input autoComplete="username" autoCapitalize="none" value={username} onChange={(e) => setUsername(e.target.value)} required pattern="[A-Za-z0-9_]{3,20}" />
        </Field>
        <Field label="Display name" hint="You can change this later.">
          <Input value={displayName} onChange={(e) => setDisplayName(e.target.value)} maxLength={32} placeholder={username} />
        </Field>
        <Field label="Password" hint="At least 6 characters, and not a common password.">
          <Input type="password" autoComplete="new-password" value={password} onChange={(e) => setPassword(e.target.value)} required minLength={6} />
        </Field>
        <ErrorBox error={error} />
        <Button type="submit" disabled={busy} className="w-full">{busy ? "Creating…" : "Create account"}</Button>
      </form>
      <p className="mt-4 text-sm text-muted">Already have an account? <Link to="/login" className="text-accent">Log in</Link></p>
    </AuthShell>
  );
}

export function ResetPage() {
  const [params] = useSearchParams();
  const token = params.get("token") ?? "";
  const [password, setPassword] = useState("");
  const [error, setError] = useState<unknown>(null);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  async function submit(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await post("/auth/reset", { token, newPassword: password });
      setDone(true);
    } catch (err) {
      setError(err);
    } finally {
      setBusy(false);
    }
  }

  return (
    <AuthShell title="Set a new password">
      {done ? (
        <>
          <p className="text-sm">Password changed. Every other session has been logged out.</p>
          <Link to="/login" className="mt-4 inline-block text-sm text-accent">Log in</Link>
        </>
      ) : !token ? (
        <p className="text-sm">This reset link is missing its token. Ask the admin for a new one.</p>
      ) : (
        <form onSubmit={submit} className="space-y-4">
          <Field label="New password" hint="At least 6 characters, and not a common password.">
            <Input type="password" autoComplete="new-password" value={password} onChange={(e) => setPassword(e.target.value)} required minLength={6} />
          </Field>
          <ErrorBox error={error} />
          <Button type="submit" disabled={busy} className="w-full">Save password</Button>
        </form>
      )}
    </AuthShell>
  );
}
